/**
 * api.js - API Service Functions
 * Handles all communication with the backend server
 */

import { config } from '../config';
import { logger } from '../logger';

/**
 * Load chat history from server
 * @param {string} token - Auth token
 * @returns {Promise<Array>} Conversations array
 */
export const loadHistoryFromServer = async (token) => {
  try {
    const response = await fetch(`${config.apiUrl}/history`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }
    
    const data = await response.json();
    return data.conversations || [];
  } catch (error) {
    logger.error('Failed to load history from server', error);
    throw error;
  }
};

/**
 * Clear all history on server
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response from server
 */
export const clearHistoryOnServer = async (token) => {
  try {
    const response = await fetch(`${config.apiUrl}/history`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    logger.error('Failed to clear history on server', error);
    throw error;
  }
};

/**
 * Fetch the list of tools available to the authenticated user
 * @param {string} token - Auth token
 * @returns {Promise<Array>} Array of tool objects { tool_id, name, description, examples }
 */
export const fetchAvailableTools = async (token) => {
  try {
    const response = await fetch(`${config.apiUrl}/tools/available`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Failed to fetch available tools', error);
    return [];
  }
};

/**
 * Delete a conversation on server
 * @param {string} token - Auth token
 * @param {string} conversationId - Conversation ID to delete
 * @returns {Promise<Object>} Response from server
 */
export const deleteConversationOnServer = async (token, conversationId) => {
  try {
    const response = await fetch(`${config.apiUrl}/history/${conversationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    logger.error('Failed to delete conversation on server', error);
    throw error;
  }
};

/**
 * Send a chat message to the server
 * @param {string} token - Auth token
 * @param {Object} payload - Chat request payload
 * @returns {Promise<Object>} Response from server
 */
export const sendChatMessage = async (token, payload, signal) => {
  // Ensure filenames are included with file data
  const requestPayload = {
    message: payload.message,
    model: payload.model,
    image_data: payload.image_data,
    pdf_data: payload.pdf_data,
    pdf_filename: payload.pdf_name || payload.pdf_filename,
    excel_data: payload.excel_data,
    excel_filename: payload.excel_name || payload.excel_filename,
    tools: payload.tools,
    tool_routing_checked: payload.tool_routing_checked || false,
    conversation_id: payload.conversation_id
  };

  const response = await fetch(`${config.apiUrl}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestPayload),
    signal,
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Ask the backend routing gate whether the prompt should open a tool UI or
 * continue as normal chat.
 * @param {string} token - Auth token
 * @param {Object} payload - Routing request payload
 * @returns {Promise<Object>} Validated route decision
 */
export const routeToolIntent = async (token, payload) => {
  const response = await fetch(`${config.apiUrl}/tools/route`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      message: payload.message,
      has_image: !!payload.has_image,
      has_pdf: !!payload.has_pdf,
      has_excel: !!payload.has_excel,
      has_video: !!payload.has_video,
      attachment_types: payload.attachment_types || [],
      conversation_id: payload.conversation_id || null
    })
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Verify token with the server
 * @param {string} token - Auth token
 * @returns {Promise<boolean>} Whether token is valid
 */
export const verifyToken = async (token) => {
  try {
    logger.apiRequest('GET', `${config.apiUrl}/verify`);
    const response = await fetch(`${config.apiUrl}/verify`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    logger.apiResponse('GET', `${config.apiUrl}/verify`, response.status);
    return response.ok;
  } catch (error) {
    logger.error('Token verification error', error);
    return false;
  }
};

// =============================================================================
// Dictionary Analysis API
// =============================================================================

/**
 * Analyze an image for dictionary-based travel intent
 * @param {string} token - Auth token
 * @param {Object} payload - Analysis request {image_url?, image_data?, poi_name?}
 * @returns {Promise<Object>} Analysis results for all question types
 */
export const analyzeDictionary = async (token, payload) => {
  try {
    logger.apiRequest('POST', `${config.apiUrl}/dictionary/analyze`);
    const response = await fetch(`${config.apiUrl}/dictionary/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Analysis failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('POST', `${config.apiUrl}/dictionary/analyze`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Dictionary analysis failed', error);
    throw error;
  }
};

/**
 * Submit approved dictionary analysis results
 * @param {string} token - Auth token
 * @param {Object} payload - Approval data with corrected scores
 * @returns {Promise<Object>} Approval confirmation
 */
export const approveDictionaryAnalysis = async (token, payload) => {
  try {
    logger.apiRequest('POST', `${config.apiUrl}/dictionary/approve`);
    const response = await fetch(`${config.apiUrl}/dictionary/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Approval failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('POST', `${config.apiUrl}/dictionary/approve`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Dictionary approval failed', error);
    throw error;
  }
};

/**
 * Get dictionary questions configuration
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Questions config with definitions and metadata
 */
export const getDictionaryQuestionsConfig = async (token) => {
  try {
    logger.apiRequest('GET', `${config.apiUrl}/dictionary/questions`);
    const response = await fetch(`${config.apiUrl}/dictionary/questions`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    logger.apiResponse('GET', `${config.apiUrl}/dictionary/questions`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Failed to get dictionary questions config', error);
    throw error;
  }
};

// =============================================================================
// Dictionary Canvas Entry API
// =============================================================================

/**
 * Save a new dictionary canvas entry
 * If entry already exists (409 Conflict), falls back to updating instead
 * @param {string} token - Auth token
 * @param {Object} payload - Canvas entry data
 * @returns {Promise<Object>} Created/updated canvas entry confirmation
 */
export const saveDictionaryCanvasEntry = async (token, payload) => {
  try {
    logger.apiRequest('POST', `${config.apiUrl}/dictionary/canvas`);
    const response = await fetch(`${config.apiUrl}/dictionary/canvas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    // Handle 409 Conflict by falling back to PUT (entry already exists)
    if (response.status === 409) {
      logger.info('Canvas entry already exists, updating instead', { entryId: payload.entry_id });
      return updateDictionaryCanvasEntry(token, payload.entry_id, payload);
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Save canvas failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('POST', `${config.apiUrl}/dictionary/canvas`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Failed to save dictionary canvas entry', error);
    throw error;
  }
};

/**
 * Update an existing dictionary canvas entry
 * @param {string} token - Auth token
 * @param {string} entryId - Canvas entry UUID
 * @param {Object} payload - Updated canvas data
 * @returns {Promise<Object>} Updated canvas entry confirmation
 */
export const updateDictionaryCanvasEntry = async (token, entryId, payload) => {
  try {
    logger.apiRequest('PUT', `${config.apiUrl}/dictionary/canvas/${entryId}`);
    const response = await fetch(`${config.apiUrl}/dictionary/canvas/${entryId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Update canvas failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('PUT', `${config.apiUrl}/dictionary/canvas/${entryId}`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Failed to update dictionary canvas entry', error);
    throw error;
  }
};

// =============================================================================
// Per-Question Approval API
// =============================================================================

/**
 * Approve a single question within a canvas entry
 * @param {string} token - Auth token
 * @param {Object} payload - Question approval data
 * @param {string} payload.canvas_entry_id - Canvas entry UUID
 * @param {string} payload.question_type - Question type (express, suspected, acknowledge)
 * @param {number} payload.score - Score value
 * @param {string} payload.reasoning - Reasoning text
 * @param {string[]} payload.detected_countries - Array of detected countries
 * @param {number} payload.confidence - Confidence value (0-1)
 * @returns {Promise<Object>} Approval confirmation with approval_id
 */
export const approveQuestion = async (token, payload) => {
  try {
    logger.apiRequest('POST', `${config.apiUrl}/dictionary/approve-question`);
    const response = await fetch(`${config.apiUrl}/dictionary/approve-question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Question approval failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('POST', `${config.apiUrl}/dictionary/approve-question`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Failed to approve question', error);
    throw error;
  }
};

/**
 * Revoke a question approval
 * @param {string} token - Auth token
 * @param {number} approvalId - The approval ID to revoke
 * @returns {Promise<Object>} Revocation confirmation
 */
export const revokeQuestionApproval = async (token, approvalId) => {
  try {
    logger.apiRequest('DELETE', `${config.apiUrl}/dictionary/approve-question/${approvalId}`);
    const response = await fetch(`${config.apiUrl}/dictionary/approve-question/${approvalId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Revoke approval failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('DELETE', `${config.apiUrl}/dictionary/approve-question/${approvalId}`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Failed to revoke question approval', error);
    throw error;
  }
};

/**
 * Get all active question approvals for a canvas entry
 * @param {string} token - Auth token
 * @param {string} canvasEntryId - Canvas entry UUID
 * @returns {Promise<Object>} Object with approvals by question type
 */
export const getQuestionApprovals = async (token, canvasEntryId) => {
  try {
    logger.apiRequest('GET', `${config.apiUrl}/dictionary/approve-question/${canvasEntryId}`);
    const response = await fetch(`${config.apiUrl}/dictionary/approve-question/${canvasEntryId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Get approvals failed: ${response.status} - ${errorText}`);
    }

    logger.apiResponse('GET', `${config.apiUrl}/dictionary/approve-question/${canvasEntryId}`, response.status);
    return await response.json();
  } catch (error) {
    logger.error('Failed to get question approvals', error);
    throw error;
  }
};

// =============================================================================
// Protest Report API — Backend Integration
// =============================================================================
// Backend calls the company Protest Report service (POST daily + GET XLSX). Secrets stay server-side.

function parseFastApiErrorBody(text, status) {
  if (!text) return `Request failed (${status})`;
  try {
    const j = JSON.parse(text);
    if (typeof j.detail === 'string') return j.detail;
    if (Array.isArray(j.detail)) {
      return j.detail.map((d) => (d && d.msg) ? d.msg : JSON.stringify(d)).join('; ');
    }
    if (j.detail != null && typeof j.detail === 'object') {
      return JSON.stringify(j.detail).slice(0, 800);
    }
    if (j.message) return String(j.message);
  } catch (_) {
    /* plain text */
  }
  return text.length > 800 ? `${text.slice(0, 800)}…` : text;
}

function createApiError(message, status) {
  const error = new Error(message);
  error.status = status;
  error.isDefinitiveNotFound = status === 404;
  return error;
}

async function createProtestMonitorApiError(response) {
  const text = await response.text();
  const detail = parseFastApiErrorBody(text, response.status);
  let code = 'REQUEST_FAILED';

  if (response.status === 401) code = 'SESSION_EXPIRED';
  else if (response.status === 403) code = 'ACCESS_DENIED';
  else if (response.status === 409) code = 'CONFIG_CONFLICT';
  else if (response.status === 400 || response.status === 422) code = 'VALIDATION_ERROR';

  const fallbackMessages = {
    SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
    ACCESS_DENIED: 'You do not have access to manage protest monitor targets.',
    CONFIG_CONFLICT: 'The monitor is scanning or the configuration changed. Reload and try again.',
    VALIDATION_ERROR: 'The target configuration is invalid.',
  };
  const message = code === 'SESSION_EXPIRED'
    ? 'SESSION_EXPIRED'
    : (
      detail && !/^Request failed \(\d+\)$/.test(detail)
        ? detail
        : (fallbackMessages[code] || detail)
    );
  const error = createApiError(message, response.status);
  error.code = code;
  return error;
}

/**
 * Load the effective global target list for the daily protest monitor.
 * @param {string} token
 * @returns {Promise<Object>}
 */
export const getProtestMonitorTargets = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/protest-city-monitor/targets`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) throw await createProtestMonitorApiError(response);
  const data = await response.json();
  return {
    ...data,
    targets: (data.targets || []).map((target) => ({
      name: target.name,
      region: target.region,
      location_scope: target.location_scope,
    })),
    recipients: (data.recipients || []).map((email) => String(email)),
  };
};

/**
 * Replace the complete global target list using optimistic concurrency.
 * @param {string} token
 * @param {Array<Object>} targets
 * @param {string|number} expectedVersion
 * @returns {Promise<Object>}
 */
export const updateProtestMonitorTargets = async (token, targets, expectedVersion, recipients) => {
  if (!token) throw new Error('Not authenticated');

  const canonicalTargets = targets.map((target) => ({
    name: target.name,
    region: target.region,
    location_scope: target.location_scope,
  }));
  const canonicalRecipients = recipients.map((email) => String(email).trim());
  const response = await fetch(`${config.apiUrl}/protest-city-monitor/targets`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      targets: canonicalTargets,
      recipients: canonicalRecipients,
      expected_version: expectedVersion,
    }),
  });

  if (!response.ok) throw await createProtestMonitorApiError(response);
  return await response.json();
};

/**
 * Request protest daily report via backend (Protest Report API).
 * @param {string} token - Auth token
 * @param {string} queryTitle - Display title (for logging only)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} location - Location hint
 * @param {Array<string>} keywords - Keywords
 * @param {Function} onProgress - (current, total) when request completes
 * @param {string|null} region - Region code; omit to use server default
 * @param {'city'|'country'} locationScope - How the location should be interpreted
 * @returns {Promise<Object>}
 */
export const sendGrokQueryParallel = async (
  token,
  queryTitle,
  startDate,
  endDate,
  location,
  keywords,
  onProgress,
  region = null,
  locationScope = 'city'
) => {
  if (!token) {
    throw new Error('Not authenticated');
  }

  try {
    logger.info('Sending protest report request to backend', {
      startDate,
      endDate,
      location,
      keywords,
      region,
      locationScope
    });

    const response = await fetch(`${config.apiUrl}/grok/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate,
        location: location || null,
        location_scope: locationScope || 'city',
        keywords: keywords && keywords.length > 0 ? keywords : null,
        region: region || null
      })
    });

    if (response.status === 401) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[ProtestReport] Backend error response:', errorText);
      const detail = parseFastApiErrorBody(errorText, response.status);
      throw new Error(detail);
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      logger.error('Protest report response was not JSON', e);
      throw new Error('Invalid response from server');
    }

    if (onProgress && data.stats) {
      onProgress(data.stats.total_queries, data.stats.total_queries);
    }

    logger.info('Protest report completed via backend', {
      success: data.success,
      totalQueries: data.stats?.total_queries,
      eventsDiscovered: data.stats?.events_discovered
    });

    return {
      success: data.success,
      response: data.response,
      stats: {
        totalQueries: data.stats?.total_queries || 0,
        successfulQueries: data.stats?.successful_queries || 0,
        dateChunks: data.stats?.date_chunks || 0,
        region: data.stats?.region,
        reportDate: data.stats?.report_date,
        eventsDiscovered: data.stats?.events_discovered,
        newInReport: data.stats?.new_in_report,
        existingInReport: data.stats?.existing_in_report,
        linksValidated: data.stats?.links_validated,
        locationsGeocoded: data.stats?.locations_geocoded,
        discoveryEventsExtracted: data.stats?.discovery_events_extracted,
        discoveryEventsAfterFilter: data.stats?.discovery_events_after_filter,
        filteredInvalidDate: data.stats?.discovery_filtered_invalid_date,
        filteredOutOfRange: data.stats?.discovery_filtered_out_of_range,
        filteredLocationMismatch: data.stats?.discovery_filtered_location_mismatch,
        filteredKeywordMismatch: data.stats?.discovery_filtered_keyword_mismatch,
        filteredLowRelevance: data.stats?.discovery_filtered_low_relevance,
        deduplicated: data.stats?.discovery_deduplicated
      }
    };
  } catch (error) {
    logger.error('Protest report request failed', error);
    throw error;
  }
};

/** Poll interval for async protest jobs (ms). */
export const PROTEST_REPORT_POLL_INTERVAL_MS = config.protestReportPollIntervalMs;
/** Max time to wait for async report before giving up (ms). */
export const PROTEST_REPORT_MAX_WAIT_MS = config.protestReportMaxWaitMs;

/**
 * Start async protest daily report (returns job id immediately; upstream POST runs in background).
 * @param {string} token
 * @param {Object} params - start_date, end_date, location, locationScope, keywords, region (camelCase)
 */
export const startProtestReportJob = async (token, params) => {
  if (!token) {
    throw new Error('Not authenticated');
  }

  const body = {
    start_date: params.startDate,
    end_date: params.endDate,
    location: params.location || null,
    location_scope: params.locationScope || 'city',
    keywords: params.keywords?.length ? params.keywords : null,
    region: params.region || null,
    conversation_id: params.conversationId || null,
    message: params.message || null,
  };

  const response = await fetch(`${config.apiUrl}/grok/search/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    const detail = parseFastApiErrorBody(errorText, response.status);
    throw createApiError(detail, response.status);
  }

  const data = await response.json();
  return {
    jobId: data.job_id,
    upstreamJobId: data.upstream_job_id || null,
    status: data.status || 'started',
    message: data.message || null,
  };
};

/**
 * Poll async protest job status (one lightweight GET per call).
 * @param {string} token
 * @param {string} jobId
 */
export const getProtestReportStatus = async (token, jobId) => {
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${config.apiUrl}/grok/search/status/${encodeURIComponent(jobId)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    const detail = parseFastApiErrorBody(errorText, response.status);
    throw createApiError(detail, response.status);
  }

  return await response.json();
};

/**
 * Get active resumable protest jobs for the authenticated user.
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getActiveProtestReportJobs = async (token) => {
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${config.apiUrl}/grok/search/jobs/active`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    const detail = parseFastApiErrorBody(errorText, response.status);
    throw new Error(detail);
  }

  const data = await response.json();
  return data.jobs || [];
};

/**
 * Send a single-location protest query via backend Protest Report API
 * Uses the same protest report endpoint with minimal parameters
 * @param {string} token - Auth token
 * @param {string} query - The formatted query string (used as location context)
 * @param {string} startDate - Optional start date (YYYY-MM-DD)
 * @param {string} endDate - Optional end date (YYYY-MM-DD)
 * @returns {Promise<Object>} Response from backend
 */
export const sendGrokQuery = async (token, query, startDate = null, endDate = null) => {
  // For single queries, use today's date if not provided
  const today = new Date().toISOString().split('T')[0];
  const effectiveStartDate = startDate || today;
  const effectiveEndDate = endDate || today;

  // Use the parallel endpoint with the query as location context
  return sendGrokQueryParallel(
    token,
    'Single Query',
    effectiveStartDate,
    effectiveEndDate,
    query,
    null,
    null,
    null
  );
};

/**
 * Submit a video file for summarization
 * @param {string} token - Auth token
 * @param {File} file - Video file to summarize
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Response with summary, metadata, transcript fields
 */
export const summarizeVideo = async (token, file, conversationId, userPrompt, sourceLanguage = null) => {
  const formData = new FormData();
  formData.append('video', file);
  if (conversationId) {
    formData.append('conversation_id', conversationId);
  }
  if (userPrompt) {
    formData.append('user_prompt', userPrompt);
  }
  if (sourceLanguage) {
    formData.append('source_language', sourceLanguage);
  }

  const response = await fetch(`${config.apiUrl}/video/summarize`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 413) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`CONTEXT_TOO_LARGE: ${errorData.detail || 'Video exceeds context window limit.'}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Submit a video URL for summarization (YouTube, TikTok, Facebook, etc.)
 * @param {string} token - Auth token
 * @param {string} url - Video URL from a social platform
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Response with summary, metadata, transcript fields
 */
export const summarizeVideoUrl = async (token, url, conversationId, userPrompt, sourceLanguage = null) => {
  const requestPayload = {
    url,
    conversation_id: conversationId || null,
    user_prompt: userPrompt || null,
    source_language: sourceLanguage || null,
  };

  const response = await fetch(`${config.apiUrl}/video/summarize-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestPayload)
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 413) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`CONTEXT_TOO_LARGE: ${errorData.detail || 'Video exceeds context window limit.'}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Score a video URL against Carinae dictionaries via AnalytiX.
 * @param {string} token - Auth token
 * @param {string} url - Video URL (e.g. TikTok)
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Response with response_text, score, matched_dictionaries, metadata
 */
export const scoreVideoUrl = async (token, url, conversationId) => {
  const requestPayload = {
    url,
    conversation_id: conversationId || null,
  };

  const response = await fetch(`${config.apiUrl}/video/score-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 413) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`CONTEXT_TOO_LARGE: ${errorData.detail || 'Video exceeds context window limit.'}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Score an uploaded video file against Carinae dictionaries.
 */
export const scoreVideoFile = async (token, file, conversationId) => {
  const formData = new FormData();
  formData.append('video', file);
  if (conversationId) {
    formData.append('conversation_id', conversationId);
  }

  const response = await fetch(`${config.apiUrl}/video/score`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 413) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`CONTEXT_TOO_LARGE: ${errorData.detail || 'Video exceeds context window limit.'}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Submit a video file for transcription only.
 * @param {string} token - Auth token
 * @param {File} file - Video file to transcribe
 * @param {string} conversationId - Conversation ID
 * @param {string|null} sourceLanguage - Optional source language override
 * @returns {Promise<Object>} Response with transcript, metadata, transcript fields
 */
export const transcribeVideo = async (token, file, conversationId, sourceLanguage = null) => {
  const formData = new FormData();
  formData.append('video', file);
  if (conversationId) {
    formData.append('conversation_id', conversationId);
  }
  if (sourceLanguage) {
    formData.append('source_language', sourceLanguage);
  }

  const response = await fetch(`${config.apiUrl}/video/transcribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Submit a video URL for transcription only.
 * @param {string} token - Auth token
 * @param {string} url - Video URL
 * @param {string} conversationId - Conversation ID
 * @param {string|null} sourceLanguage - Optional source language override
 * @returns {Promise<Object>} Response with transcript, metadata, transcript fields
 */
export const transcribeVideoUrl = async (token, url, conversationId, sourceLanguage = null) => {
  const response = await fetch(`${config.apiUrl}/video/transcribe-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      url,
      conversation_id: conversationId || null,
      source_language: sourceLanguage || null
    })
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Analyze a video or image file for deepfake content.
 * @param {string} token - Auth token
 * @param {File} file - Video or image file to analyze
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Response with verdict, confidence, report and metadata
 */
export const analyzeDeepfake = async (token, file, conversationId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (conversationId) {
    formData.append('conversation_id', conversationId);
  }

  const response = await fetch(`${config.apiUrl}/deepfake/analyze`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 413) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`CONTEXT_TOO_LARGE: ${errorData.detail || 'File exceeds size limit.'}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Analyze a video or image URL for deepfake content.
 * @param {string} token - Auth token
 * @param {string} url - Video URL (YouTube, TikTok, etc.) or direct image URL
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Response with verdict, confidence, report and metadata
 */
export const analyzeDeepfakeUrl = async (token, url, conversationId) => {
  const requestPayload = {
    url,
    conversation_id: conversationId || null,
  };

  const response = await fetch(`${config.apiUrl}/deepfake/analyze-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestPayload)
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 413) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`CONTEXT_TOO_LARGE: ${errorData.detail || 'File exceeds size limit.'}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Backend error: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

/**
 * Check Protest Report API configuration via backend status endpoint
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Status information
 */
export const getGrokStatus = async (token) => {
  if (!token) {
    return { configured: false, error: 'Not authenticated' };
  }

  try {
    const response = await fetch(`${config.apiUrl}/grok/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      return { configured: false, error: `Status check failed: ${response.status}` };
    }

    return await response.json();
  } catch (error) {
    logger.error('Protest report status check failed', error);
    return { configured: false, error: error.message };
  }
};

/** Poll interval for async deep research jobs (ms). */
export const DEEP_RESEARCH_POLL_INTERVAL_MS = 8000;

export const startDeepResearchJob = async (token, params) => {
  if (!token) throw new Error('Not authenticated');

  const body = {
    query: params.query,
    scope: params.scope || null,
    depth: params.depth || 'standard',
    sources: params.sources || { web: true, communications: true, attachments: true },
    conversation_id: params.conversationId || null,
    user_message: params.userMessage || params.query,
    pdf_data: params.pdfData || null,
    pdf_filename: params.pdfFilename || null,
    excel_data: params.excelData || null,
    excel_filename: params.excelFilename || null,
    report_type: params.report_type || null,
  };

  const response = await fetch(`${config.apiUrl}/deep-research/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status || 'started' };
};

export const getDeepResearchStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/deep-research/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

export const getActiveDeepResearchJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/deep-research/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return data.jobs || [];
};

export const cancelDeepResearchJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/deep-research/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

export const streamDeepResearchEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/deep-research/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

/** Alias for Phase 07 API naming — same as streamDeepResearchEvents. */
export const subscribeDeepResearchEvents = streamDeepResearchEvents;

// =============================================================================
// GMS Report API — three-phase pipeline (/gms-report/)
// Completely separate from the deep-research pipeline.
// =============================================================================

/** Poll interval for async GMS report jobs (ms). */
export const GMS_REPORT_POLL_INTERVAL_MS = 8000;

/**
 * Start a GMS Daily Intelligence Brief job.
 * No query parameters needed – the backend derives everything from config.
 * @param {string} token
 * @param {Object} params - { conversationId }
 * @returns {Promise<{jobId: string, status: string}>}
 */
export const startGmsReportJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/gms-report/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status || 'started' };
};

/**
 * Poll GMS report job status (fallback when SSE is unavailable).
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const getGmsReportStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/gms-report/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Get active GMS report jobs for the authenticated user.
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getActiveGmsReportJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/gms-report/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return data.jobs || [];
};

/**
 * Cancel a running GMS report job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const cancelGmsReportJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/gms-report/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Stream GMS report job events via Server-Sent Events.
 * Same signature and contract as streamDeepResearchEvents.
 * @param {string} token
 * @param {string} jobId
 * @param {Function} onEvent - called with each parsed event object
 * @param {Function} isCancelled - returns true when the caller wants to stop
 */
export const streamGmsReportEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/gms-report/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`GMS SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// Aviation Report API — three-phase pipeline (/aviation-report/)
// Completely separate from deep-research and GMS pipelines.
// =============================================================================

/** Poll interval for async Aviation Report jobs (ms). */
export const AVIATION_REPORT_POLL_INTERVAL_MS = 8000;

/**
 * Start an Aviation Sector Executive Daily Brief job.
 * No query parameters needed – the backend derives everything from config.
 * @param {string} token
 * @param {Object} params - { conversationId }
 * @returns {Promise<{jobId: string, status: string}>}
 */
export const startAviationReportJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/aviation-report/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      conversation_id: params.conversationId || null,
    }),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status };
};

/**
 * Get the status of an Aviation Report job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const getAviationReportStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/aviation-report/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

/**
 * Get active Aviation Report jobs for the authenticated user.
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getActiveAviationReportJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/aviation-report/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

/**
 * Cancel a running Aviation Report job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const cancelAviationReportJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/aviation-report/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

/**
 * Retry infographic and PPTX generation without rerunning the aviation report.
 */
export const retryAviationReportArtifacts = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/aviation-report/jobs/${encodeURIComponent(jobId)}/artifacts/retry`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

/**
 * Download the durable aviation PPTX through the authenticated backend.
 */
export const downloadAviationReportPptx = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/aviation-report/jobs/${encodeURIComponent(jobId)}/artifacts/pptx`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  const disposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
  return {
    blob: await response.blob(),
    filename: filenameMatch?.[1] || `aviation-executive-brief-${jobId}.pptx`,
  };
};

/**
 * Stream Aviation Report job events via Server-Sent Events.
 * Same signature and contract as streamGmsReportEvents.
 * @param {string} token
 * @param {string} jobId
 * @param {Function} onEvent - called with each parsed event object
 * @param {Function} isCancelled - returns true when the caller wants to stop
 */
export const streamAviationReportEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/aviation-report/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`Aviation SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// Energy Report API — three-phase pipeline (/energy-report/)
// Completely separate from deep-research, GMS, and Aviation pipelines.
// =============================================================================

/** Poll interval for async Energy Report jobs (ms). */
export const ENERGY_REPORT_POLL_INTERVAL_MS = 8000;

/**
 * Start an Energy Sector Executive Daily Brief job.
 * No query parameters needed – the backend derives everything from config.
 * @param {string} token
 * @param {Object} params - { conversationId }
 * @returns {Promise<{jobId: string, status: string}>}
 */
export const startEnergyReportJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/energy-report/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status };
};

/**
 * Get the status of an Energy Report job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const getEnergyReportStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/energy-report/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

/**
 * Get active Energy Report jobs for the authenticated user.
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getActiveEnergyReportJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/energy-report/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

/**
 * Cancel a running Energy Report job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const cancelEnergyReportJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/energy-report/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

/**
 * Stream Energy Report job events via Server-Sent Events.
 * Same signature and contract as streamAviationReportEvents.
 * @param {string} token
 * @param {string} jobId
 * @param {Function} onEvent - called with each parsed event object
 * @param {Function} isCancelled - returns true when the caller wants to stop
 */
export const streamEnergyReportEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/energy-report/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`Energy SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// Data Centers Report API — executive daily brief (/data-center-report/)
// =============================================================================

/** Poll interval for async Data Centers Report jobs (ms). */
export const DATA_CENTER_REPORT_POLL_INTERVAL_MS = 8000;

export const startDataCenterReportJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/data-center-report/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status };
};

export const getDataCenterReportStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/data-center-report/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

export const getActiveDataCenterReportJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/data-center-report/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

export const cancelDataCenterReportJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/data-center-report/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

export const streamDataCenterReportEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/data-center-report/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`Data Centers SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// Gaming Report API — weekly executive brief (/gaming-report/)
// =============================================================================

export const GAMING_REPORT_POLL_INTERVAL_MS = 8000;

export const startGamingReportJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/gaming-report/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status };
};

export const getGamingReportStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/gaming-report/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

export const getActiveGamingReportJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/gaming-report/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

export const cancelGamingReportJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/gaming-report/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

export const streamGamingReportEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/gaming-report/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`Gaming report SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// US-China Trade Report API — weekly dispute update (/us-china-trade-report/)
// =============================================================================

export const US_CHINA_TRADE_REPORT_POLL_INTERVAL_MS = 8000;

export const startUsChinaTradeReportJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/us-china-trade-report/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status };
};

export const getUsChinaTradeReportStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/us-china-trade-report/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

export const getActiveUsChinaTradeReportJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/us-china-trade-report/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

export const cancelUsChinaTradeReportJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/us-china-trade-report/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }
  return response.json();
};

export const streamUsChinaTradeReportEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/us-china-trade-report/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`US-China trade report SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// LATAM Summary API — user-supplied URLs → structured crime incident summary
// =============================================================================

/** Poll interval for async LATAM summary jobs (ms). */
export const LATAM_SUMMARY_POLL_INTERVAL_MS = 8000;

/**
 * Start a LATAM summary job for the given article URLs.
 * @param {string} token
 * @param {Object} params - { urls: string[], conversationId?: string }
 * @returns {Promise<{jobId: string, status: string}>}
 */
export const startLatamSummaryJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/latam-summary/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      urls: params.urls || [],
      conversation_id: params.conversationId || null,
    }),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status || 'started' };
};

/**
 * Poll LATAM summary job status (fallback when SSE is unavailable).
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const getLatamSummaryStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/latam-summary/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Get active LATAM summary jobs for the authenticated user.
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getActiveLatamSummaryJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/latam-summary/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return data.jobs || [];
};

/**
 * Cancel a running LATAM summary job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const cancelLatamSummaryJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/latam-summary/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Stream LATAM summary job events via Server-Sent Events.
 * Same signature and contract as streamGmsReportEvents.
 * @param {string} token
 * @param {string} jobId
 * @param {Function} onEvent - called with each parsed event object
 * @param {Function} isCancelled - returns true when the caller wants to stop
 */
export const streamLatamSummaryEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/latam-summary/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`LATAM summary SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// LATAM-to-CSV API — pasted incident report → validated TSV
// =============================================================================

export const LATAM_TO_CSV_POLL_INTERVAL_MS = 3000;

export const startLatamToCsvJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${config.apiUrl}/latam-to-csv/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      report_text: params.reportText || '',
      conversation_id: params.conversationId || null,
    }),
  });
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  const data = await response.json();
  return { jobId: data.job_id, status: data.status || 'started' };
};

export const getLatamToCsvStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(
    `${config.apiUrl}/latam-to-csv/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  return await response.json();
};

export const getActiveLatamToCsvJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${config.apiUrl}/latam-to-csv/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

export const cancelLatamToCsvJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(
    `${config.apiUrl}/latam-to-csv/jobs/${encodeURIComponent(jobId)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
  );
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  return await response.json();
};

export const streamLatamToCsvEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/latam-to-csv/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok || !response.body) {
    throw new Error(`LATAM-to-CSV SSE stream failed: ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((item) => item.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // Ignore malformed event chunks; polling remains the fallback.
      }
    }
  }
  reader.cancel().catch(() => {});
};

// =============================================================================
// Sequoia Structure API — pasted source report → GMS Daily Intelligence Brief
// =============================================================================

export const SEQUOIA_STRUCTURE_POLL_INTERVAL_MS = 3000;

export const startSequoiaStructureJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${config.apiUrl}/sequoia-structure/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      report_text: params.reportText || '',
      conversation_id: params.conversationId || null,
    }),
  });
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  const data = await response.json();
  return { jobId: data.job_id, status: data.status || 'started' };
};

export const getSequoiaStructureStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(
    `${config.apiUrl}/sequoia-structure/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  return await response.json();
};

export const getActiveSequoiaStructureJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${config.apiUrl}/sequoia-structure/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  const data = await response.json();
  return data.jobs || [];
};

export const cancelSequoiaStructureJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(
    `${config.apiUrl}/sequoia-structure/jobs/${encodeURIComponent(jobId)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
  );
  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    throw new Error(parseFastApiErrorBody(await response.text(), response.status));
  }
  return await response.json();
};

export const streamSequoiaStructureEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/sequoia-structure/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok || !response.body) {
    throw new Error(`Sequoia Structure SSE stream failed: ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((item) => item.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // Ignore malformed event chunks; polling remains the fallback.
      }
    }
  }
  reader.cancel().catch(() => {});
};

// =============================================================================
// TWA Summary API — async article summarization (URLs and/or pasted text)
// =============================================================================

/** Poll interval for async TWA summary jobs (ms). */
export const TWA_SUMMARY_POLL_INTERVAL_MS = 8000;

/**
 * Start a TWA summary job for the given article URLs and/or pasted texts.
 * @param {string} token
 * @param {Object} params - { urls?: string[], pastedTexts?: string[], template?: string, conversationId?: string }
 * @returns {Promise<{jobId: string, status: string}>}
 */
export const startTwaSummaryJob = async (token, params = {}) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/twa-summary/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      urls: params.urls || [],
      pasted_texts: params.pastedTexts || [],
      pdf_documents: params.pdfDocuments || [],
      template: params.template || null,
      conversation_id: params.conversationId || null,
    }),
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return { jobId: data.job_id, status: data.status || 'started' };
};

/**
 * Poll TWA summary job status (fallback when SSE is unavailable).
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const getTwaSummaryStatus = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/twa-summary/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Return all active TWA summary jobs for the current user.
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getActiveTwaSummaryJobs = async (token) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${config.apiUrl}/twa-summary/jobs/active`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();
  return data.jobs || [];
};

/**
 * Cancel a running TWA summary job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<Object>}
 */
export const cancelTwaSummaryJob = async (token, jobId) => {
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(
    `${config.apiUrl}/twa-summary/jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Stream TWA summary job events via Server-Sent Events.
 * Same signature and contract as streamLatamSummaryEvents.
 * @param {string} token
 * @param {string} jobId
 * @param {Function} onEvent - called with each parsed event object
 * @param {Function} isCancelled - returns true when the caller wants to stop
 */
export const streamTwaSummaryEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/twa-summary/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`TWA summary SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// PDF Task API — async background processing for large PDFs (> async_page_threshold)
// =============================================================================

/** Poll interval for async PDF task jobs (ms). */
export const PDF_TASK_POLL_INTERVAL_MS = 5000;

/**
 * Get the current status of an async PDF task job.
 * @param {string} token
 * @param {string} jobId
 * @returns {Promise<{job_id, status, step, message, percent, result_text, error}>}
 */
export const getPdfTaskStatus = async (token, jobId) => {
  const response = await fetch(
    `${config.apiUrl}/pdf-task/status/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};

/**
 * Stream PDF task job events via Server-Sent Events.
 * Same signature and contract as streamDeepResearchEvents.
 * @param {string} token
 * @param {string} jobId
 * @param {Function} onEvent - called with each parsed event object
 * @param {Function} isCancelled - returns true when the caller wants to stop
 */
export const streamPdfTaskEvents = async (token, jobId, onEvent, isCancelled) => {
  const response = await fetch(
    `${config.apiUrl}/pdf-task/events/${encodeURIComponent(jobId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok || !response.body) {
    throw new Error(`PDF task SSE stream failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!isCancelled?.()) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      try {
        onEvent?.(JSON.parse(line.slice(6)));
      } catch {
        // ignore malformed chunks
      }
    }
  }

  reader.cancel().catch(() => {});
};

// =============================================================================
// Statistical Summary — POST /statistical-summary/analyze
// =============================================================================

/**
 * Submit one or two CSV files to the Statistical Summary pipeline.
 *
 * @param {string} token          Auth token
 * @param {File}   csvCurrent     Current-week CSV file (required)
 * @param {File|null} csvPrevious Previous-week CSV file (optional)
 * @returns {Promise<Object>}
 */
export const analyzeStatisticalSummary = async (token, csvCurrent, csvPrevious = null) => {
  if (!token) throw new Error('Not authenticated');

  const form = new FormData();
  form.append('csv_current', csvCurrent);
  if (csvPrevious) {
    form.append('csv_previous', csvPrevious);
  }

  const response = await fetch(`${config.apiUrl}/statistical-summary/analyze`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  const data = await response.json();

  return {
    a1CurrentHtml:  data.a1_current_html  ?? null,
    a3Html:         data.a3_html          ?? null,
    a1PreviousHtml: data.a1_previous_html ?? null,
    a2Html:         data.a2_html          ?? null,
    dateRange:      data.date_range       ?? '',
    topActor:       data.top_actor        ?? 'Unknown',
    total:          data.total            ?? 0,
  };
};

/**
 * Decode a base64-encoded HTML string into a blob URL.
 * The caller owns the URL and is responsible for eventually calling
 * URL.revokeObjectURL() when the resource is no longer needed.
 *
 * @param {string} b64Html  base64-encoded UTF-8 HTML
 * @returns {string}        blob: URL
 */
export const createHtmlBlobUrl = (b64Html) => {
  const bytes = atob(b64Html);
  const arr   = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    arr[i] = bytes.charCodeAt(i);
  }
  const blob = new Blob([arr], { type: 'text/html;charset=utf-8' });
  return URL.createObjectURL(blob);
};

/**
 * Decode a base64-encoded HTML string and open it in a new tab via a blob URL.
 *
 * NOTE: browsers block window.open() calls that happen after an await (not
 * within a synchronous user-gesture stack).  Use createHtmlBlobUrl() + embed
 * the URL as a link in the chat message so the user can always click through.
 *
 * @param {string} b64Html  base64-encoded UTF-8 HTML
 * @param {string} label    window name / tab title hint
 * @returns {Window|null}   the opened window object (null if pop-up was blocked)
 */
export const openHtmlBase64InNewTab = (b64Html, label) => {
  const blobUrl = createHtmlBlobUrl(b64Html);
  const win     = window.open(blobUrl, label || '_blank');
  // If the pop-up was blocked win is null; the blob URL is kept alive so the
  // caller can still embed it as a link.  Revoke after 10 minutes.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 10 * 60 * 1000);
  return win;
};

/**
 * Generate a portrait infographic PNG from a PDF document.
 *
 * @param {string} token      Auth token
 * @param {File}   pdfFile    PDF File object
 * @param {string} focus      Optional focus hint (empty string = full document)
 * @param {string} detail     'standard' | 'detailed'
 * @param {string} conversationId Chat conversation to persist the result under
 * @returns {Promise<{image_base64: string, image_url?: string, filename: string}>}
 */
export const generateInfographic = async (
  token,
  pdfFile,
  focus = '',
  detail = 'standard',
  conversationId = ''
) => {
  if (!token) throw new Error('Not authenticated');

  const form = new FormData();
  form.append('pdf_file', pdfFile);
  if (focus) {
    form.append('focus', focus);
  }
  form.append('detail', detail);
  if (conversationId) {
    form.append('conversation_id', conversationId);
  }

  const response = await fetch(`${config.apiUrl}/infographic/generate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (response.status === 401) throw new Error('SESSION_EXPIRED');
  if (response.status === 403) throw new Error('Access restricted: your account is not authorised for this feature.');
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(parseFastApiErrorBody(errorText, response.status));
  }

  return await response.json();
};
